/**
 * Registr akci - JEDINE misto seznamu akci platformy (brief kroku 8 bod 6).
 * Jmenny prostor: `context.entity.action` (04 §2).
 *
 * Tridy akci ridi dve veci:
 *  - `requiresApproval()` => rezim vykonu pro autonomni volani (MCP gateway,
 *    krok 19): AUTO | PREPARE | APPROVAL | HUMAN_ONLY (threat model §4 krok 6).
 *  - dokumentacni matice (matrix.ts) a review politiky jako tabulky (ADR-07).
 *
 * Nove akce pridavej SEM; test matic (test/policy-matrix.spec.ts) selze, dokud
 * akce nema radek v ocekavanych efektech i v allowlistach roli (policies.ts).
 */
import { PolicyConfigError } from './errors.js';
function define(name, classes, extra) {
    const parts = name.split('.');
    if (parts.length !== 3 || parts.some((p) => p.length === 0)) {
        throw new Error(`Akce '${name}' nema tvar context.entity.action`);
    }
    const [, entity, verb] = parts;
    if (!entity || !verb) {
        // Nemelo by nastat - validace delek vyse; pojistka pro noUncheckedIndexedAccess.
        throw new Error(`Akce '${name}' ma prazdny segment`);
    }
    if (classes.length === 0) {
        throw new Error(`Akce '${name}' musi mit alespon jednu tridu`);
    }
    return { name, entity, verb, classes, bulk: extra?.bulk };
}
// ---- identity --------------------------------------------------------------
export const IDENTITY_ACTIONS = [
    define('identity.member.invite', ['admin']),
    define('identity.member.update', ['admin']),
    define('identity.member.deactivate', ['admin']),
    define('identity.tenant.update', ['admin']),
    define('identity.office.update', ['admin']),
    define('identity.team.update', ['admin']),
    define('identity.auditlog.read', ['read', 'admin']),
    define('identity.token.create', ['admin']),
];
// ---- party -----------------------------------------------------------------
export const PARTY_ACTIONS = [
    define('party.profile.read', ['read']),
    define('party.profile.create', ['write']),
    define('party.profile.update', ['write']),
    define('party.profile.delete', ['delete']),
    define('party.consent.update', ['write']),
    // Doklady / rodna cisla / PEP - citliva vrstva hlidana i klasifikaci.
    // Verb 'sensitive_read' na teze entite 'profile' - typ zdroje se nemeni.
    define('party.profile.sensitive_read', ['read']),
];
// ---- property ---------------------------------------------------------------
export const PROPERTY_ACTIONS = [
    define('property.property.read', ['read']),
    define('property.property.create', ['write']),
    define('property.property.update', ['write']),
    define('property.property.delete', ['delete']),
    define('property.property.export', ['export']),
    define('property.valuation.write', ['write']),
    // Publikace na externi portaly = WRITE_EXTERNAL (schvalovana).
    define('property.listing.publish', ['write_external']),
    define('property.listing.unpublish', ['write']),
];
// ---- case -------------------------------------------------------------------
export const CASE_ACTIONS = [
    define('case.case.read', ['read']),
    define('case.case.create', ['write']),
    define('case.case.update', ['write']),
    define('case.case.delete', ['delete']),
    define('case.case.assign', ['admin']),
    define('case.case.close', ['write']),
    define('case.case.export', ['export']),
];
// ---- documents --------------------------------------------------------------
export const DOCUMENT_ACTIONS = [
    define('documents.document.upload', ['write']),
    define('documents.document.read', ['read']),
    // Stazeni = read + export (exfilni hrozba, threat model §57 položka 8):
    // autonomne jen se schvalenim, cteni v UI nezavisli na tomto rezimu.
    define('documents.document.download', ['read', 'export']),
    define('documents.document.update', ['write']),
    define('documents.document.delete', ['delete']),
    // Podpisova akce - LEGAL (krok podpisu strany, OTP step-up na portalu).
    define('documents.document.sign_request', ['legal', 'communicate']),
    define('documents.template.manage', ['admin']),
    // Krok T3: smlouvy zakazky (vazba na case/property, zive pole, rucni
    // upravy, verze) a sablony kancelare (import + mapovani, katalog "+ vlastni").
    define('documents.contract.read', ['read']),
    define('documents.contract.manage', ['write']),
    // Krok RBAC1 bod 2: cteni vzoru kancelare je podklad pro zalozeni smlouvy
    // (katalog + detail), ne sprava mustru. Proto samostatna READ akce — jinak
    // by si kazdy, kdo smi smlouvu zalozit, musel vzit i pravo mustry menit.
    define('documents.contract_template.read', ['read']),
    define('documents.contract_template.manage', ['write']),
    // Krok KP1: klientsky formular pres magic link. `manage` je COMMUNICATE,
    // protoze zaklada a POSILA odkaz klientovi mimo system (e-mail/SMS) -
    // autonomne tedy jen se schvalenim; v MCP registru neni vubec
    // (plugin deklaruje HUMAN_ONLY, tyz rezim jako u podpisove akce).
    define('documents.client_intake.read', ['read']),
    define('documents.client_intake.manage', ['write', 'communicate']),
    // Krok ES1: elektronicky podpis smluv. `manage` je COMMUNICATE, protoze
    // zaklada a POSILA podepisovy odkaz klientovi mimo system (viz
    // ROZHODNUTI ES1 v db-service.ts). `sign` je pravni akt s právnimi
    // úcinky — proto LEGAL + samostatná trida, aby ji agent nedostal
    // automaticky (v MCP registru je HUMAN_ONLY, stejne jako
    // `documents.document.sign_request`).
    define('documents.signature.read', ['read']),
    define('documents.signature.manage', ['write', 'communicate']),
    define('documents.signature.sign', ['legal', 'communicate']),
    // Nastaveni kanceláře: jaky typ dokumentu se podepisuje jak. ADMIN — je to
    // nastaveni, ktere meni právní rámec kanceláře, ne běžná editace.
    define('documents.signature.configure', ['admin']),
];
// ---- finance ----------------------------------------------------------------
export const FINANCE_ACTIONS = [
    define('finance.commission.read', ['read']),
    // QAFIX6 (nalez N02, QA-KOD-20261021): `POST /v1/finance/commissions` zada
    // `finance.commission.create`, ne `.update` - bez teto registrace padala
    // routa na PolicyConfigError (500) pri kazdem volani.
    define('finance.commission.create', ['money']),
    define('finance.commission.update', ['money']),
    define('finance.invoice.create', ['write']),
    // QAFIX6: cteni faktur (`GET /v1/finance/invoices`, `/:id`) - akce v registru
    // chybela, takze oba endpointy vracely VZDY 500 (nalez N02).
    define('finance.invoice.read', ['read']),
    define('finance.invoice.send', ['write_external']),
    define('finance.payment.read', ['read']),
    define('finance.payment.create', ['money']),
    // Samotny akt schvaleni platby nikdy autonomne (human_only).
    define('finance.payment.approve', ['money', 'admin']),
    define('finance.refund.create', ['money']),
    // QAFIX6 — uschova (`POST /v1/finance/escrow`, `/:id/release`): penezni
    // operace kancelare, stejna trida jako platby. Akce v registru chybely.
    define('finance.escrow.create', ['money']),
    define('finance.escrow.update', ['money']),
    // QAFIX6 — predplatne kancelare u nas (Stripe plan, `GET/PUT /v1/finance/
    // billing`). NENI to `finance.billing_settings.*` (to je „kdo fakturuje
    // klientovi"); tohle je vlastni ucet kancelare, proto jen owner/admin.
    define('finance.billing.read', ['read']),
    define('finance.billing.update', ['admin']),
    define('finance.report.read', ['read']),
    define('finance.report.export', ['export']),
    // Oficialni podani (datova schranka) - LEGAL + WRITE_EXTERNAL.
    define('finance.databox.send', ['legal', 'write_external']),
    // Naklady na nemovitost (R5) - jednotlive zaznamy nakladu per property/case.
    define('finance.cost.read', ['read']),
    define('finance.cost.create', ['money']),
    define('finance.cost.update', ['money']),
    define('finance.cost.delete', ['delete']),
    // Agregovany prehled napric zakazkami/makleri (owner/pm_manager/finance).
    define('finance.cost_summary.read', ['read']),
    // Ciselnik kategorii + alert % - owner/admin nastaveni.
    define('finance.cost_category.manage', ['admin']),
    // Krok FAK1 - fakturujici subjekt (kdo fakturuje) a dodavatel v MyUctu.
    define('finance.billing_settings.read', ['read']),
    define('finance.billing_settings.update', ['admin']),
    // Sprava dodavatele KANCELARE nebo (s explicitnim brokerUserId) za jineho
    // maklere - viz `service.ts` resolveBrokerUserId. Owner/admin/finance.
    define('finance.supplier.manage', ['money']),
    // Sebeobsluha maklere v rezimu 'broker' - jen VLASTNI dodavatel, scope
    // hlida service vrstva (`resolveBrokerUserId`), ne ABAC.
    define('finance.supplier.manage_own', ['money']),
    // Krok FAK2 - doklady pro klienty (provize, sluzby). Plny rozsah
    // (kterykoli dodavatel v tenantu) vs jen VLASTNI dodavatel maklere v
    // rezimu 'broker' - scope hlida service vrstva (`resolveIssuerSupplier`),
    // stejny vzor jako `finance.supplier.manage(_own)`.
    define('finance.client_invoice.manage', ['money']),
    define('finance.client_invoice.manage_own', ['money']),
    // Katalog sluzeb kancelare (reporty, poradenstvi...) - sprava je admin
    // nastaveni, cteni potrebuje kazdy, kdo smi vystavit doklad.
    define('finance.service_catalog.manage', ['admin']),
    define('finance.service_catalog.read', ['read']),
    // Krok FAK3 - sprava najmu (predpisy najemneho, mesicni davka, uhrady,
    // upominky, rocni vyuctovani sluzeb dle z. 67/2013). Stejne deleni rozsahu
    // jako u `finance.client_invoice.*`: plny rozsah kancelare vs jen VLASTNI
    // dodavatel maklere (scope hlida service vrstva, ne ABAC).
    define('finance.rent.manage', ['money']),
    define('finance.rent.manage_own', ['money']),
    define('finance.rent.read', ['read']),
];
// ---- pm (property management) -----------------------------------------------
export const PM_ACTIONS = [
    define('pm.lease.read', ['read']),
    define('pm.lease.create', ['write']),
    define('pm.lease.update', ['write']),
    define('pm.lease.terminate', ['legal']),
    define('pm.ticket.read', ['read']),
    define('pm.ticket.create', ['write']),
    define('pm.ticket.update', ['write']),
    define('pm.ticket.close', ['write']),
    // Objednavka udrzby = penize ze spravovane nemovitosti.
    define('pm.maintenance_order.create', ['money']),
    define('pm.rent_invoice.issue', ['money', 'write_external']),
];
// ---- communication ----------------------------------------------------------
export const COMMUNICATION_ACTIONS = [
    // COMMUNICATE vyzaduje lidske schvaleni autonomniho vykonu (threat model §4).
    define('communication.email.send', ['communicate']),
    define('communication.email.bulk', ['communicate'], { bulk: true }),
    define('communication.message.create', ['communicate']),
    define('communication.message.read', ['read']),
    define('communication.thread.read', ['read']),
    define('communication.thread.create', ['communicate']),
    define('communication.thread.update', ['communicate']),
    define('communication.domain.create', ['write']),
    define('communication.domain.read', ['read']),
    define('communication.domain.update', ['write']),
    define('communication.template.create', ['write']),
    define('communication.template.read', ['read']),
    define('communication.case.create', ['write']),
    define('communication.unsubscribe.create', ['write']),
];
// ---- workflow ---------------------------------------------------------------
// Workflow engine (krok 10, ADR-04): guardy prechodu volaji policy.authorize.
// Rozhodnuti o schvaleni (approve/reject) = write trida; kdo smi rozhodovat,
// ridi allowlisty roli (policies.ts). Start/submity pouzivaji existujici akce
// prislusne domeny (napr. documents.document.update).
export const WORKFLOW_ACTIONS = [define('workflow.approval.decide', ['write'])];
// ---- acquisition (krok 26) ----------------------------------------------------
export const ACQUISITION_ACTIONS = [
    define('acquisition.lead.create', ['write']),
    define('acquisition.lead.read', ['read']),
    define('acquisition.lead.update', ['write']),
    define('acquisition.lead.delete', ['delete']),
    define('acquisition.audit.create', ['write']),
    define('acquisition.audit.read', ['read']),
    define('acquisition.seller_pack.create', ['write']),
];
// ---- CRM (krok CRM1) --------------------------------------------------------
//
// Poptavka/zajemec v pipeline. `demand.import` je tridy `write_external` -
// data PRIHAZI z venkovniho systemu (portál), takze autonomni vykon (MCP
// gateway) ji bere jako schvalovanou (PREPARE), ne AUTO. Rucni zalozeni
// poptavky je bezny `write` a PREPARE - makler ji stejne chce potvrdit
// (kontakt + hledane parametry jsou osobni udaje, GDPR).
export const CRM_ACTIONS = [
    define('crm.demand.read', ['read']),
    define('crm.demand.create', ['write']),
    define('crm.demand.update', ['write']),
    define('crm.demand.delete', ['delete']),
    define('crm.demand.import', ['write_external']),
    // Konfigurace sloupcu pipeline je nastaveni kancelare (jine sloupce = jiny
    // obchodni model), proto trida `admin` jako `case.case.assign`.
    define('crm.demand_stage.manage', ['admin']),
    // Krok SR6: web kancelare (zapnuti/stav) je nastaveni kancelare (narok =
    // rocni tarif), ne makleruv obchod - stejna trida jako `demand_stage.manage`.
    define('crm.office_site.manage', ['admin']),
    // Krok CRM2: ukoly maklere (Trello tabule). `task.generate` NESAHÁ na data
    // z venku - jen scanuje, co uz REOS ma, a zaluzuje z toho ukoly, takze je
    // bezny `write`, ne `write_external` (jine nez `demand.import`, ktery data
    // opravdu tahne z portalu). Je to vsak `admin`, ne `write`: spusteni
    // generatoru zahlty desetky az tisice radku po cele kancelari, a to je
    // prace vedouciho, ne maklere. Makler si generator pustit nemuzi - ma na to
    // DOPLNENI tlacitko v UI, ktere volaji stejne chranene omezeni role.
    define('crm.task.read', ['read']),
    define('crm.task.create', ['write']),
    define('crm.task.update', ['write']),
    define('crm.task.delete', ['delete']),
    define('crm.task.generate', ['admin']),
    // Krok CRM3: kalendar maklere (prohlidky/podpisy/predani/schuzky/ukoly
    // s terminem) + odběr do Google/Outlooku + e-mailové připomínky.
    // `calendar_feed.manage` je `write`, NE `admin`: je to odkaz pro sebe,
    // ne běh po celé kanceláři. Odeslání připomínek naopak `admin` - stejně
    // jako `task.generate`, sken sahá na všechny makléře najednou.
    define('crm.calendar_event.read', ['read']),
    define('crm.calendar_event.create', ['write']),
    define('crm.calendar_event.update', ['write']),
    define('crm.calendar_event.delete', ['delete']),
    define('crm.calendar_feed.manage', ['write']),
    define('crm.calendar_reminder.send', ['admin']),
    // Krok CRM4: prohlidky pres odkaz + parovani poptavek s nabidkami.
    // `viewing.manage` (vypsani terminu, zalozeni odkazu, zruseni rezervace)
    // je bezny `write` nad vlastnim kalendarem. ODESLANI cehokoli klientovi
    // (`viewing.invite` = pozvanka s odkazem, `offer.send` = nabidka) je
    // `communicate`: autonomni volani (MCP) ho nikdy neodesle samo, a v UI
    // jde ven az po nahledu a potvrzeni maklere (zadani: „nic se neodešle bez
    // potvrzení makléře"). `match.update` = prepocet / odmitnuti shody.
    define('crm.viewing.read', ['read']),
    define('crm.viewing.manage', ['write']),
    define('crm.viewing.invite', ['communicate']),
    define('crm.match.read', ['read']),
    define('crm.match.update', ['write']),
    define('crm.offer.send', ['communicate']),
    // Krok NOTIF1: fronta upozorneni maklere. Cte a oznacuje PRECTENE kazdy
    // sam za sebe (`crm_notifications.user_id` = prihlaseny uzivatel), proto
    // `read`/`write`, ne `admin`. Nastaveni souhrnu a vychoziho maklere
    // (`notification_settings.manage`) je naopak nastaveni KANCELARE — tatáž
    // trida jako `demand_stage.manage`.
    define('crm.notification.read', ['read']),
    define('crm.notification.update', ['write']),
    define('crm.notification_settings.manage', ['admin']),
];
// ---- audit (krok 18) --------------------------------------------------------
export const AUDIT_ACTIONS = [
    define('audit.log.read', ['read', 'admin']),
    define('audit.log.export', ['export', 'admin']),
    define('audit.anchor.manage', ['admin']),
    define('audit.chain.verify', ['read', 'admin']),
];
// ---- audit nemovitosti (AI due diligence, AUDIT-ARCHITEKTURA faze 0+1) ------
//
// POZOR na jmenny prostor: `audit.*` uz nese platformni auditni stopa vyse
// (audit.log.*, audit.anchor.*, audit.chain.*). Kolize NENI na urovni jmen -
// entita se lisi - ale je matouci. Rozhodnuti o pripadnem prejmenovani
// kontextu je v docs/agent-briefs/REPORT-audit-faze-0-1.md.
export const PROPERTY_AUDIT_ACTIONS = [
    define('dd.case.create', ['write']),
    define('dd.case.read', ['read']),
    define('dd.case.update', ['write']),
    // Sprava ucastniku pripadu (krok A1) - RBAC pusti broker/assistant, ale
    // service.ts navic vyzaduje OWNER pripadu (nebo globalni owner/admin),
    // aby libovolny ucastnik nemohl pridavat/odebirat jine (viz NotCaseOwnerError).
    define('dd.case.manage_participants', ['write']),
    define('dd.fact.record', ['write']),
    define('dd.question.manage', ['write']),
    define('dd.geometry.lock', ['write']),
    // Tvrzeni maklere (krok G4) - USER_INPUT fakt s autorem/casem, muze spustit
    // odhad demolice. Jen BROKER/ADMIN (viz policies.ts) - je to obchodni
    // rozhodnuti o pripadu, ne vyzkum.
    define('dd.claim.record', ['write']),
    // Krok A4 (Korespondent): ODESLANI dotazu spravci siti / SVJ / obci schvaluje
    // clovek. Trida `communicate` (odchazi posta jmenem kancelare treti strane);
    // route je navic `HUMAN_ONLY` pro MCP - agent korespondenci neuvolnuje
    // (CLAUDE.md §1 bod 10).
    define('dd.correspondence.approve', ['communicate']),
    // Zamitnuti navrhu s POVINNYM duvodem. Nic neodchazi, proto jen `write`.
    define('dd.correspondence.reject', ['write']),
    // write_external: odchozi volani na CUZK/RUIAN => autonomni beh jen se schvalenim.
    define('dd.research.run', ['write', 'write_external']),
    // read_external (D2, open-world od C2): fetch verejneho obsahu pres
    // classifyAccess (duediligence access-model). Cteni verejnych dat svet nemeni.
    define('dd.research.fetch_open', ['read_external']),
];
// ---- commerce (krok C1) ------------------------------------------------------
/**
 * Objednavky a platby reportu. `order.create` je trida `money` - autonomni
 * vykon (MCP) tedy vzdy jen se schvalenim; verejna objednavka na uvodni
 * strance zadny princip nema a tuhle cestu nepouziva (jde pres BotGuard
 * + rate limit, viz plugin commerce kontextu).
 *
 * `order.mark_paid` a `order.refund` jsou rucni zasahy do penez - navic
 * trida `admin`, takze je v RBAC ma jen owner/admin (viz policies.ts).
 */
export const COMMERCE_ACTIONS = [
    define('commerce.product.read', ['read']),
    define('commerce.order.read', ['read']),
    define('commerce.order.create', ['write', 'money']),
    define('commerce.order.mark_paid', ['money', 'admin']),
    define('commerce.order.refund', ['money', 'admin']),
    // Krok C3: rucni doruceni objednavky, ktera ceka ve fronte NEEDS_REVIEW
    // (kvalita behu neprosla branou) - stejna trida jako mark_paid/refund,
    // owner/admin only, POVINNY duvod (viz plugin.ts + order-service.ts).
    define('commerce.order.force_deliver', ['money', 'admin']),
    // Krok A5: UVOLNENI reportu zakaznikovi (role Revizor). Teprve tahle akce
    // posila odkaz na PDF - do te doby report lezi ve fronte "Ke schvaleni".
    // Trida `communicate`, protoze odchazi e-mail zakaznikovi, + `admin`
    // (owner/admin only, stejny dosah jako force_deliver).
    define('commerce.report.release', ['communicate', 'admin']),
    // Vraceni reportu k prepracovani (novy beh auditu) - POVINNY duvod.
    define('commerce.report.return', ['write', 'admin']),
    // Krok PL1: cteni pilotni kampane/kuponu kancelare ("stav a cerpani
    // v adminu", zadani bod 2). Jen CTENI - zalozeni je vyhradne RedHand ops
    // skript (asSystem), zadna HTTP cesta nema pravo kampan/kupon VYTVORIT
    // (viz REPORT-step-PL1.md §2). Trida `admin`: obsahuje obchodni udaje
    // (ICO, cerpani kuponu), ne bezny provozni udaj kazdeho makleru.
    define('commerce.campaign.read', ['read', 'admin']),
];
// ---- registr ----------------------------------------------------------------
export const ACTION_DEFS = [
    ...IDENTITY_ACTIONS,
    ...PARTY_ACTIONS,
    ...PROPERTY_ACTIONS,
    ...CASE_ACTIONS,
    ...DOCUMENT_ACTIONS,
    ...FINANCE_ACTIONS,
    ...PM_ACTIONS,
    ...COMMUNICATION_ACTIONS,
    ...WORKFLOW_ACTIONS,
    ...AUDIT_ACTIONS,
    ...ACQUISITION_ACTIONS,
    ...CRM_ACTIONS,
    ...PROPERTY_AUDIT_ACTIONS,
    ...COMMERCE_ACTIONS,
];
/** Jmena vsech registrovanych akci. */
export const ACTIONS = ACTION_DEFS.map((a) => a.name);
const ACTION_INDEX = new Map(ACTION_DEFS.map((a) => [a.name, a]));
/** Vrati definici akce nebo undefined (pro neexistujici jmeno). */
export function findAction(name) {
    return ACTION_INDEX.get(name);
}
/** Definice existujici akce; neexistujici jmeno = programatorska chyba. */
export function requireAction(name) {
    const def = ACTION_INDEX.get(name);
    if (!def) {
        // PolicyConfigError (ne obecny Error): volajici (engine/can) ji nesmi
        // spolknout jako autorizacni zamitnuti.
        throw new PolicyConfigError(`Neznama akce '${name}' - registruj ji v actions.ts`);
    }
    return def;
}
//# sourceMappingURL=actions.js.map